import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { twMerge } from 'tailwind-merge';

import { Select } from '@/_components';
import { SelectorWithAvatarContainer } from '@/_containers';
import type { ContactQueryLinkerSchema, ContactQuerySchema } from '@/_pages/contacts/shared';
import { useGetContactsMutation } from '@/_pages/contacts/shared';
import { useGetTeamMembersMutation } from '@/_pages/team-members/shared';
import type { TeamMemberQueryLinkerSchema } from '@/_schemas/team-members/team-members-query-linker-schema';
import { nameResolver, useBoolean, useDebounce } from '@/_shared';

import { useRecruitingFormContext } from '../../form-context';
import type { ReferralTypeSchema } from '../../schemas';

type ReferralComboboxProps = {
  disabled?: boolean;
  className?: string;
};

type ReferralOptionType = TeamMemberQueryLinkerSchema | ContactQueryLinkerSchema;

type ReferralGroupedOption = {
  label: string;
  options: ReferralOptionType[];
};

export function ReferralCombobox({ disabled = false, className }: ReferralComboboxProps) {
  const form = useRecruitingFormContext();
  const [referral] = form.watch(['referral']);

  const [isFocused, focus] = useBoolean();
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 500);
  const fetchDefaultOptionsRef = useRef(true);

  const {
    mutateAsync: getTeamMembersOptions,
    data: teamMembersData,
    isPending: isLoadingTeamMembers,
  } = useGetTeamMembersMutation({
    onSuccess: () => {
      focus.setFalse();
    },
  });
  const {
    mutateAsync: getContactsOptions,
    data: contactsData,
    isPending: isLoadingContacts,
  } = useGetContactsMutation();

  const formatGroupLabel = useCallback((data: ReferralGroupedOption) => <div>{data.label}</div>, []);
  const getOptionLabel = useCallback(
    (option: ReferralOptionType) => nameResolver(option?.first_name, option?.last_name, 'Referral'),
    []
  );
  const getOptionValue = useCallback((option: ReferralOptionType) => option?.id.toString(), []);

  const fetchOptions = useCallback(
    (search = '', per_page = 100) => {
      if ((!isFocused || disabled) && !search) {
        return;
      }
      getTeamMembersOptions({ search, per_page });
      getContactsOptions({ search, per_page });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disabled, isFocused]
  );

  const updateValue = useCallback(
    (val: TeamMemberQueryLinkerSchema | ContactQueryLinkerSchema | null, type: ReferralTypeSchema | null) => {
      form.setValue('referral', val, { shouldDirty: true });
      form.setValue('referral_type', type, { shouldDirty: true });
      form.setValue('referral_id', val?.id, { shouldDirty: true });
    },
    [form]
  );

  const handleChange = useCallback(
    (v: ReferralOptionType | null) => {
      if (!v) {
        updateValue(null, null);
      } else if (isTeamMember(v)) {
        updateValue(v, 'team-member');
      } else if (isContact(v)) {
        updateValue(v, 'contact');
      }
    },
    [updateValue]
  );

  const getLogo = useCallback(
    (provider: TeamMemberQueryLinkerSchema | ContactQueryLinkerSchema | null | undefined): string => {
      let src: string | undefined = '';
      if (provider) {
        if (isTeamMember(provider)) {
          src = provider?.profile_image?.at(0)?.url;
        }
        if (isContact(provider)) {
          src = provider?.profile_image?.at(0)?.url;
        }
      }
      return src ?? '';
    },
    []
  );

  useEffect(() => {
    if (fetchDefaultOptionsRef.current) {
      fetchOptions();
      fetchDefaultOptionsRef.current = false;
    }
  }, [fetchOptions]);

  useEffect(() => {
    if (!fetchDefaultOptionsRef.current) {
      fetchOptions(debouncedQuery);
    }
  }, [fetchOptions, debouncedQuery]);

  const groupedOptions = useMemo(
    (): ReferralGroupedOption[] => [
      {
        label: 'Team Members',
        options: teamMembersData || [],
      },
      {
        label: 'Contacts',
        options: contactsData || [],
      },
    ],
    [teamMembersData, contactsData]
  );

  return (
    <SelectorWithAvatarContainer
      disabled={disabled}
      src={getLogo(referral)}
      label="Referral"
      name={nameResolver(referral?.first_name, referral?.last_name, 'Referral')}
      className={twMerge(className, 'lg:flex-row')}
    >
      <Select<ReferralOptionType, false, ReferralGroupedOption>
        label="Referral"
        value={referral}
        options={groupedOptions}
        formatGroupLabel={formatGroupLabel}
        onInputChange={setQuery}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        onChange={handleChange}
        onFocus={focus.setTrue}
        onBlur={focus.setFalse}
        isLoading={isLoadingTeamMembers || isLoadingContacts}
        disabled={disabled}
      />
    </SelectorWithAvatarContainer>
  );
}

function isTeamMember(option: ReferralOptionType): option is TeamMemberQueryLinkerSchema {
  return 'first_name' in option;
}

function isContact(option: ReferralOptionType): option is ContactQuerySchema {
  return 'linked_in_url' in option;
}
